import React from 'react';
import { useUser } from '@/stores/authStore';
import { OfferCounterInfo } from '@/types/offer';
import { PoolDetail } from '@/types/pool';
import { PoolStatus } from '@/types/pool';
import { DOCUMENT_TYPE_CONFIG, DocumentType } from '@/types/pool/documentTypes';
import { extractFilename } from '@/utils/misc';
import { getAcceptedCounterOfferInfo } from '@/utils/poolStatus';
import { isInvestor } from '@/utils/user';
import { CounterInfoField } from './CounterInfoField';
import { DocumentCard } from './DocumentCard';

type PoolDocumentsContentProps = {
  pool: PoolDetail;
  counterOfferInfo: OfferCounterInfo | null;
};

export const PoolDocumentsContent: React.FC<PoolDocumentsContentProps> = ({ pool, counterOfferInfo }) => {
  const allDocuments = pool.documents || [];
  const user = useUser();
  const userIsInvestor = isInvestor(user);

  // Find counter offer
  const acceptCounterOfferInfo = getAcceptedCounterOfferInfo(pool.offers);
  const counterRequiredDocuments =
    counterOfferInfo?.required_documents || acceptCounterOfferInfo?.required_documents || [];

  // Group documents by type (excluding photos and other documents)
  const remainingDocuments = new Map(
    allDocuments
      .filter((doc) => doc.doc_type !== DocumentType.PHOTO && doc.doc_type !== DocumentType.OTHER)
      .map((doc) => [doc.doc_type, doc])
  );
  const otherDocuments = allDocuments.filter((doc) => doc.doc_type === DocumentType.OTHER);

  const allDocumentTypes = Object.values(DocumentType).filter(
    (type) => type !== DocumentType.PHOTO && (type !== DocumentType.OTHER || otherDocuments.length > 0)
  );

  // Separate documents from photos
  const photos = allDocuments.filter((doc) => doc.doc_type === DocumentType.PHOTO);

  const isInEscrow = pool.status === PoolStatus.IN_ESCROW;

  const photosRequestedInCounter = counterRequiredDocuments.includes(DocumentType.PHOTO);

  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <>
      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6">
        {allDocumentTypes.map((docType) => {
          const hasCounterOffer = counterRequiredDocuments.includes(docType);
          if (docType === DocumentType.OTHER) {
            return otherDocuments.map((doc, index) => (
              <DocumentCard
                key={`${docType}-${index}`}
                title={doc.title || 'Additional Document'}
                description={DOCUMENT_TYPE_CONFIG[docType].description}
                filename={extractFilename(doc)}
                onDownload={() => handleDownload(doc.url)}
                showCounterBadge={hasCounterOffer}
                hasCounterOffer={hasCounterOffer}
                isInvestor={userIsInvestor}
                isInEscrow={isInEscrow}
              />
            ));
          }
          const document = remainingDocuments.get(docType);

          if (userIsInvestor && !document) {
            return null;
          }

          return (
            <DocumentCard
              key={docType}
              title={DOCUMENT_TYPE_CONFIG[docType].label}
              description={DOCUMENT_TYPE_CONFIG[docType].description}
              filename={document ? extractFilename(document) : undefined}
              onDownload={document ? () => handleDownload(document.url) : undefined}
              showCounterBadge={hasCounterOffer}
              hasCounterOffer={hasCounterOffer}
              isInvestor={userIsInvestor}
              isInEscrow={isInEscrow}
            />
          );
        })}
      </div>

      {/* Property Images Section */}
      <div className="w-full flex flex-col justify-start items-start gap-4 mt-6">
        <div
          className="text-[#B2B2B2] text-base font-medium break-words"
          style={{
            fontFamily: 'var(--ep-font-avenir)',
          }}
        >
          Property Photos
        </div>
        {photosRequestedInCounter && (
          <CounterInfoField value="Investor is requesting more photos" color="text-[#D1863B]" showArrow={false} />
        )}
        <div className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-3">
          {photos.map((photo, index) => (
            <div
              key={photo.url || index}
              className="w-full h-20 bg-gray-100 rounded-lg border border-gray-200 relative overflow-hidden cursor-pointer"
              onClick={() => handleDownload(photo.url)}
            >
              <img
                src={photo.url}
                alt={`Property image ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to placeholder if image fails to load
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  const placeholder = target.parentElement?.querySelector('.placeholder');
                  if (placeholder) {
                    (placeholder as HTMLElement).style.display = 'flex';
                  }
                }}
              />
              <div
                className="placeholder w-full h-full bg-gradient-to-br from-[#E4EFFF] to-[#F4F4F4] hidden justify-center items-center"
                style={{ display: 'none' }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z"
                    fill="#B2B2B2"
                  />
                </svg>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
